<template>
  <section class="h-full mx-4" id="materialsManagement">
    <BaseTable
      v-if="materials"
      :headers="materialsHeaders"
      :list="materials"
      :total-records="materialsTotalCount"
      :items-per-page="materialsItemsPerPage"
      :selected-page="materialsPageNumber"
      table-id="materials-management"
      clickable
      @refresh-data="onRefreshData"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewMaterial"
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item as Material)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('material.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getMaterialsWithConditions, removeMaterialsByIds } from '@/services/api/materials';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type { Material } from '@/types/Materials';
import type { TableFilterData } from '@/types/Base';
import { useI18n } from 'vue-i18n';
import { useStateStore } from '@/stores/state';
const { t: $t } = useI18n({ useScope: 'global' });

const materials = ref<Material[]>();
const materialsTotalCount = ref<number>();
const materialsItemsPerPage = ref(50);
const materialsPageNumber = ref(1);
const filterIsActive = ref<boolean | undefined>(undefined);

const materialsHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'smallCode', text: $t('material.smallCode'), type: 'text', active: true },
  { value: 'smallName', text: $t('material.smallName'), type: 'text', active: true },
  { value: 'sequence', text: $t('baseObject.sequence'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('materials-management');
  if (storedTableData) {
    materialsItemsPerPage.value = storedTableData.itemsPerPage;
    materialsPageNumber.value = storedTableData.page;
  }
  const res = await getMaterialsWithConditions({
    itemsPerPage: materialsItemsPerPage.value,
    sortColumn: 'name',
    sortAscending: true,
    page: materialsPageNumber.value,
  });
  if (res) {
    materials.value = res.data;
    materialsTotalCount.value = res.count;
  }
});

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? materialsHeaders[data.column]?.value : 'name';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  const res = await getMaterialsWithConditions(params);
  if (res) {
    materials.value = res.data;
    materialsTotalCount.value = res.count;
  }
}

const toastStore = useToastStore();

async function onDeleteSingle() {
  if (!materialToDeleteSingle.value) return;
  const idToDelete = materialToDeleteSingle.value.id;

  const res = await removeMaterialsByIds([idToDelete]);
  if (!res && materials.value)
    materials.value = materials.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  confirmDeleteSingleModalActive.value = false;
}

async function onDeleteMultiselect(items: Material[]) {
  const idsToDelete = items.map((item) => item.id);
  const res = await removeMaterialsByIds(idsToDelete);

  if (!res && materials.value)
    materials.value = materials.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const confirmDeleteSingleModalActive = ref(false);
const materialToDeleteSingle = ref<Material>();

function deleteSingleConfirm(item: Material) {
  materialToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

const router = useRouter();

function onAddNewMaterial() {
  router.push({ name: 'materials-management-preview', params: { id: uuidv4() } });
}

function onItemClick(item: Material) {
  router.push({ name: 'materials-management-preview', params: { id: item.id } });
}

function onResetFilters() {
  filterIsActive.value = undefined;
  onRefreshData({});
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}
</script>
